<!--
 Copyright (c) Qwoach LLC 2020. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited
 Proprietary and confidential
 Created by Artem Bogush <artem@qwoach.com> on 8/25/20
-->

<template>
	<FormElementTemplate
		v-if="maxScore > 0 || isEditMode"
		class="form-element-score-result-view"
		:hideDescription="true"
		:formElement="formElement"
		:viewMode="viewMode"
		v-bind="$attrs"
		>

		<template #title>
			<div>
				<div v-if="isEditMode" class="form-title">Score Results</div>
			</div>
		</template>
		<template #hint>
			Score Result
			<ToolTipView :analyticsProps="tooltipAnalyticsProps">
				Define multiple outcomes depending on the client's score for this form.
				<br/><br/>
				Add score ranges and the result that corresponds to each range.
				<br/><br/>
				The result will be shown only after submitting the form.
				<br/><br/>
			</ToolTipView>
		</template>

		<template #body>

			<div :class="{'pt-5': isEditMode}">
				<div v-if="isEditMode">
					<div v-if="maxScore === 0" class="text-center pt-4">
						Please add at least one "Multiple choice" question to this form with enabled scoring to proceed.
					</div>

					<div v-else>

						<ScoreResultRangeView
							v-for="(rangeItem, index) in formElement.scoreResultRangeItems" :key="index"
							v-model="formElement.scoreResultRangeItems[index]"
							:readonly="!formItem.isEditable || !formElement._isSelected"
							class="mb-2"
							:max="maxScore"
							:index="index"
							:showAsBuckets="formElement.resultAsBuckets"
							@change="notifyElementChanged"
							@delete="onDeleteRange(index)"/>

						<qw-button @click="onAddRange()" v-if="formItem.isEditable && formElement._isSelected">{{addScoreRangeButtonText}}</qw-button>
						<div v-if="!formElement._isSelected" class="text-muted "><small>Show result as <span>{{formElement.isResultPercentage ? "percentage" : "total points"}}</span></small></div>
					</div>

				</div>
				<div v-else-if="isViewMode" class="mb-4">
					<div>
						<div v-if="formElement.resultAsBuckets" class="text-left">
							<tr v-for="(rangeItem, index) in formElement.scoreResultRangeItems" :key="index">
								<td style="max-width: 300px;vertical-align: middle;" class="title-1">{{ formElement.getBucketTitle(index) }}:</td>
								<td style="vertical-align: middle;" class="px-3"> <span>{{formElement.getBucketValueFormatted(index)}}</span></td>
							</tr>
						</div>
						<div v-else>
							<div class="font-size-6">Your score is: <span>{{ this.answersScore }}</span></div>
							<div v-if="answerScoreLabel">Result: {{ answerScoreLabel }}</div>
						</div>
					</div>
				</div>


				<!--			<FormTextRow-->
				<!--				class="form-answer"-->
				<!--				:class="{'form-answer-edit': isEditMode, 'form-answer': !isEditMode}"-->
				<!--				v-model="formElement.answer"-->
				<!--				:readonly="!isAnswerMode"-->
				<!--				:borderDotted="isEditMode"-->
				<!--				:multiline="isMultiline"-->
				<!--				:placeholder="answerPlaceholder"-->
				<!--				@change="$emit('change', formElement)"-->
				<!--			/>-->
			</div>
		</template>

		<template #menuOptionsLeft>
			<div>
				<div class="qw-row">
					<qw-checkbox v-model="formElement.resultAsBuckets" @change="onMultipleBucketChanged">
						Show results as buckets
					</qw-checkbox>
					<ToolTipView>
						When enabled, clients will see all the outcomes aka "buckets" and the total score they've earned for each bucket.
						<br/><br/>For each question the buckets will be calculated like this: if answer #1 is selected, it's score will be added to the outcome #1, if answer #2 is selected, it's score will be added to the outcome #2, etc.
						<br/><br/>
						If switched off, clients will be presented with a single outcome based on their score and the corresponding interval it falls into.
					</ToolTipView>
				</div>
				<div class="qw-row">
					<qw-checkbox v-model="formElement.isResultPercentage" @change="notifyElementChanged">
						Show client result as percentage
					</qw-checkbox>
					<ToolTipView>
						When enabled, it will show the result as a percentage of the maximum score. Calculated as "client score / maximum score * 100". Example: 6 points out of 10 will give the result of 60%.
						<br/><br/>
						If switched off the result will be shown as a score points.
					</ToolTipView>
				</div>
			</div>

		</template>

	</FormElementTemplate>
</template>

<script>

import FormElementScoreResult
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Additional/FormElementScoreResult";
import BaseFormElement from "@/core/modules/coach/Resources/Forms/FormElements/BaseFormElementView.vue";
import FormElementTemplate from "@/core/modules/coach/Resources/Forms/FormElements/FormElementTemplate.vue";
import ScoreResultRangeView from "@/core/modules/coach/Resources/Forms/FormElements/ScoreResultRangeView.vue";
import FormItem from "@/core/modules/coach/Resources/Forms/model/FormItem";
import ToolTipView from "@/core/components/Form/Base/ToolTipView.vue";

export default {
	name: "FormElementScoreResultView",
	components: {ToolTipView, ScoreResultRangeView, FormElementTemplate},
	extends: BaseFormElement,
	props: {
		formElement: {FormElementScoreResult, required: true},
		formItem: {FormItem, required: true},
		tooltipAnalyticsProps: Object,
	},

	data() {
		return {}
	},

	created(){
		this.formItem.recalculateScore()
	},


	methods: {

		onAddRange() {
			this.formElement.addRange()
			this.notifyElementChanged()
		},

		onDeleteRange(index) {
			this.formElement.scoreResultRangeItems.splice(index, 1)
			this.notifyElementChanged()
		},

		async onMultipleBucketChanged() {
			// if(this.formElement.resultAsBuckets){
			// 	await messageDialog({
			// 		title: "How it works?",
			// 		message: "When you enable 'Display all outcomes', each answer your client gives adds a score to the corresponding outcome. When the form is completed, your client will be presented with the list of all outcomes and the corresponding score they've earned for each answer.<br/><br/>Make sure to revisit your form and assign the corresponding outcome to each relevant answer option.",
			// 	})
			// }
			this.notifyElementChanged()
		}

	},

	computed: {

		maxScore() {
			let score = 0

			this.formItem.formElements.forEach(formElement => {
				let maxScore = 0
				if (formElement.isScoringEnabled() && formElement.answerOptionsScoring) {
					maxScore = formElement.calculateMaxScore()
					// if(formElement.type === FormElementType.CHECKBOXES){
					// 	maxScore = formElement.answerOptionsScoring.reduce((sum, score) => sum + score, 0)
					// } else {
					// 	maxScore = Math.max(...formElement.answerOptionsScoring)
					// }
					console.log("FormElementScoreResultView.maxScore: maxScore = ", maxScore, ", formElement = ", formElement)
				}

				score += maxScore
			})

			// eslint-disable-next-line vue/no-side-effects-in-computed-properties
			this.formElement.maxValue = score


			return score
		},


		answersScore() {
			return this.formItem.getScoreResultsOrRecalculate()
		},

		answerScoreLabel() {
			return this.formElement.getScoreTitleByScore(this.answersScore)
		},

		addScoreRangeButtonText() {
			return this.formElement.resultAsBuckets ? "+ Add bucket" : "+ Add Score Range"
		}


	}

}
</script>

<style lang="scss">
.form-element-score-result-view {
	.hover:hover {
		background: initial;
	}

	.el-slider__button {
		background-color: #fff !important;
	}

}

</style>
